<template>
  <DialogComponent
    ref="modal"
    dialogId="delivery-options-modal"
    type="bottomsheet"
  >
    <div class="p-5">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-xl font-bold text-slate-800">{{ activeTitle }}</h2>
      </div>

      <!-- Current location preview (if already selected) -->
      <div
        v-if="activeCurrentLocation"
        class="mb-4 flex items-start gap-3 p-3 rounded-xl bg-orange-50 border border-orange-200"
      >
        <svg class="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <div class="flex-1 min-w-0">
          <div class="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-0.5">Adresse de livraison</div>
          <div class="text-sm text-orange-800 font-medium truncate">
            {{ activeCurrentLocation.address || activeCurrentLocation.locationText || 'Position enregistrée' }}
          </div>
          <div v-if="activeCurrentLocation.contactName" class="text-xs text-orange-600 mt-0.5">
            {{ activeCurrentLocation.contactName }}
            <span v-if="activeCurrentLocation.contactPhone"> · {{ activeCurrentLocation.contactPhone }}</span>
          </div>
        </div>
      </div>

      <!-- Delivery Options -->
      <div class="space-y-3">
        <!-- Select on map -->
        <div
          class="flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer"
          :class="
            selectedOption === 'actualPosition'
              ? 'border-orange-400 bg-orange-50 shadow-md'
              : 'border-slate-100 bg-white hover:border-slate-200'
          "
          @click="selectOption('actualPosition')"
        >
          <div
            class="w-10 h-10 rounded-lg flex items-center justify-center"
            :class="
              selectedOption === 'actualPosition'
                ? 'bg-orange-500'
                : 'bg-slate-100'
            "
          >
            <svg
              class="w-5 h-5"
              :class="
                selectedOption === 'actualPosition'
                  ? 'text-white'
                  : 'text-slate-600'
              "
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <div class="flex-1">
            <span
              class="font-semibold"
              :class="
                selectedOption === 'actualPosition'
                  ? 'text-orange-700'
                  : 'text-slate-800'
              "
            >
              {{ activeCurrentLocation ? 'Modifier sur la carte' : 'Sélectionner sur la carte' }}
            </span>
            <span v-if="activeCurrentLocation" class="block text-xs text-slate-500 mt-0.5">
              Repositionner ou ajuster l'adresse
            </span>
          </div>
          <div
            v-if="selectedOption === 'actualPosition'"
            class="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center"
          >
            <svg
              class="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="3"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <!-- From address book -->
        <div
          class="flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer"
          :class="
            selectedOption === 'other'
              ? 'border-orange-400 bg-orange-50 shadow-md'
              : 'border-slate-100 bg-white hover:border-slate-200'
          "
          @click="selectOption('other')"
        >
          <div
            class="w-10 h-10 rounded-lg flex items-center justify-center"
            :class="
              selectedOption === 'other' ? 'bg-orange-500' : 'bg-slate-100'
            "
          >
            <svg
              class="w-5 h-5"
              :class="
                selectedOption === 'other' ? 'text-white' : 'text-slate-600'
              "
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <div class="flex-1">
            <span
              class="font-semibold"
              :class="
                selectedOption === 'other'
                  ? 'text-orange-700'
                  : 'text-slate-800'
              "
            >
              Depuis mon carnet d'adresse
            </span>
          </div>
          <div
            v-if="selectedOption === 'other'"
            class="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center"
          >
            <svg
              class="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="3"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <!-- Self Pickup -->
        <div v-if="false"
          class="flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer"
          :class="
            selectedOption === 'manualyTook'
              ? 'border-orange-400 bg-orange-50 shadow-md'
              : 'border-slate-100 bg-white hover:border-slate-200'
          "
          @click="selectOption('manualyTook')"
        >
          <div
            class="w-10 h-10 rounded-lg flex items-center justify-center"
            :class="
              selectedOption === 'manualyTook'
                ? 'bg-orange-500'
                : 'bg-slate-100'
            "
          >
            <svg
              class="w-5 h-5"
              :class="
                selectedOption === 'manualyTook'
                  ? 'text-white'
                  : 'text-slate-600'
              "
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              />
            </svg>
          </div>
          <div class="flex-1">
            <span
              class="font-semibold"
              :class="
                selectedOption === 'manualyTook'
                  ? 'text-orange-700'
                  : 'text-slate-800'
              "
            >
              Je viens chercher moi-même
            </span>
          </div>
          <div
            v-if="selectedOption === 'manualyTook'"
            class="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center"
          >
            <svg
              class="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="3"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>
      </div>

      <!-- Keep current location button (if already selected) -->
      <div v-if="activeCurrentLocation" class="mt-5 pt-2">
        <button
          class="w-full buttonComponent text-lg! font-bold! hover:scale-[1.02] active:scale-[0.98]"
          @click="keepCurrentLocation"
        >
          Confirmer cette position
        </button>
      </div>
    </div>
  </DialogComponent>
</template>

<script setup>
import { ref, computed } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';

const props = defineProps({
  currentOption: {
    type: String,
    default: 'actualPosition'
  },
  requestKey: {
    type: String,
    default: 'cart_delivery_location'
  },
  title: {
    type: String,
    default: 'Mode de livraison'
  }
});

const emit = defineEmits(['optionSelected', 'locationConfirmed']);

const modal = ref(null);
const selectedOption = ref(props.currentOption);
const activeRequestKey = ref(props.requestKey);
const activeTitle = ref(props.title);
const activeDefaultPosition = ref(null);
const activeDefaultValues = ref(null);
// The currently saved location to display as preview
const activeCurrentLocation = ref(null);

const userStore = window.$stores?.user;
const currentUser = computed(() => userStore?.state?.current);

const showModal = (options = {}) => {
  selectedOption.value = options.currentOption || props.currentOption;
  activeRequestKey.value = options.requestKey || props.requestKey || 'cart_delivery_location';
  activeTitle.value = options.title || props.title || 'Mode de livraison';
  activeDefaultPosition.value = options.defaultPosition || null;
  activeDefaultValues.value = options.defaultValues || null;
  activeCurrentLocation.value = options.currentLocation || null;
  modal.value?.show();
};

const hideModal = () => {
  modal.value?.hide();
};

const keepCurrentLocation = () => {
  if (!activeCurrentLocation.value) return;

  const confirmedLocation = {
    ...activeCurrentLocation.value,
    lat:
      activeCurrentLocation.value.lat ??
      activeCurrentLocation.value.coordinates?.lat ??
      activeDefaultPosition.value?.lat ??
      0,
    lng:
      activeCurrentLocation.value.lng ??
      activeCurrentLocation.value.coordinates?.lng ??
      activeDefaultPosition.value?.lng ??
      0,
    address:
      activeCurrentLocation.value.address ||
      activeCurrentLocation.value.locationText ||
      'Position sélectionnée',
    contactName:
      activeCurrentLocation.value.contactName ||
      activeDefaultValues.value?.contactName ||
      '',
    contactPhone:
      activeCurrentLocation.value.contactPhone ||
      activeDefaultValues.value?.contactPhone ||
      '',
    description:
      activeCurrentLocation.value.description ||
      activeDefaultValues.value?.description ||
      ''
  };

  bus.emit(constants.EVENTS.MAP.LOCATION_SELECTED, {
    requestKey: activeRequestKey.value,
    selectedLocation: confirmedLocation
  });

  emit('optionSelected', selectedOption.value || 'actualPosition');
  emit('locationConfirmed', confirmedLocation);
  hideModal();
};

const selectOption = (option) => {
  selectedOption.value = option;

  // If map selection is chosen, trigger location picker immediately
  if (option === 'actualPosition') {
    setTimeout(() => {
      bus.emit(constants.EVENTS.MAP.LOCATION_SELECTION_REQUIRED, {
        requestKey: activeRequestKey.value,
        additionalFields: [
          {
            key: 'contactName',
            label: 'Nom du contact',
            type: 'text',
            placeholder: 'Votre nom',
            required: true,
            userDefaultValue: currentUser.value
              ? `${currentUser.value.firstName || ''} ${currentUser.value.lastName || ''}`.trim()
              : ''
          },
          {
            key: 'contactPhone',
            label: 'Téléphone à contacter',
            type: 'tel',
            placeholder: '+229 XX XX XX XX',
            required: true,
            userDefaultValue: currentUser.value?.phoneNumber || ''
          }
        ],
        defaultPosition: activeDefaultPosition.value,
        defaultValues: activeDefaultValues.value
      });
    }, 100);
  }

  if (option === 'other') {
    setTimeout(() => {
      bus.emit('delivery_location_selection_requested_from_book', {
        requestKey: activeRequestKey.value
      });
    }, 100);
  }

  // Emit immediately and close dialog
  emit('optionSelected', option);
  hideModal();

  // For "manualyTook" and "other", the parent component will handle the logic
};

defineExpose({
  showModal,
  hideModal
});
</script>
